/** Support/contact email used across the app (Contact page, already-claimed modals, etc.) */
export const SUPPORT_EMAIL = 'nick@pharmaconnectnavigator.com';

export const VALIDATION_MESSAGES = {
    BUDGET_RANGE_REQUIRED: 'Please select at least one Budget Range',
    OPERATING_REGION_REQUIRED: 'Please select at least one Operating Region',
    /** Step 2 of the CDMO intake — headed "Primary Modalities" since the Aug 2026 relabel. */
    PRIMARY_MODALITY_REQUIRED: 'Please select at least one primary modality',
    BATCH_SIZE_REQUIRED: 'Please select at least one typical batch size',
    DEVELOPMENT_STAGE_REQUIRED: 'Please select a valid development stage',
    ROLE_REQUIRED: 'Please select a valid role',
    COMPANY_NAME_REQUIRED: 'Company name is required',
    NAME_REQUIRED: 'Contact name is required',
    PROFILE_ALREADY_CLAIMED_TITLE: 'Profile Already Claimed',
    PROFILE_ALREADY_CLAIMED_MESSAGE: 'The CDMO profile you are trying to claim has already been verified and linked to an account. If you believe this is an error, please contact your company administrator or our support team.',
    INVALID_TRANSFER_TOKEN: 'Invalid transfer token',
    TRANSFER_TOKEN_EXPIRED: 'Transfer token has expired. Please request a new transfer.',
    TRANSFER_RECIPIENT_MISMATCH: (email: string) => `This transfer is intended for ${email}. Please use that email address to claim.`,
    UNAUTHORIZED_CLAIM: 'Your email address is not authorized to claim this profile. Please use an official company email.',
    TRANSACTION_ERROR: 'An error occurred during transaction. Please try again.',
    CLAIM_TOKEN_EXPIRED: 'This claim link has expired. Request a new claim link from Pharma Connect Navigator support team',
    LINK_ALREADY_CLAIMED_TITLE: 'This Profile Has Already Been Claimed',
    LINK_ALREADY_CLAIMED: `The CDMO profile you're trying to claim has already been assigned to a verified representative.\n\nIf you believe this is an error or need access to this profile, please contact support at ${SUPPORT_EMAIL}.`,
    EMAIL_ALREADY_REGISTERED_TO_CDMO: 'This email is already associated with another CDMO profile and cannot claim this one.',
    PROFILE_ALREADY_CLAIMED: 'The CDMO profile you are trying to claim has already been verified and linked to an account. If you believe this is an error, please contact your company administrator or our support team.',
    ACCOUNT_CLAIMED_BY_ANOTHER: 'The account is already claimed by another user',
    TRANSFER_LINK_INVALID: 'Transfer link is invalid or expired.',
    TRANSFER_ALREADY_COMPLETED: 'This CDMO profile has already been claimed by another authorised user. If you believe this is incorrect, please contact your organisation administrator.',
    TRANSFER_LINK_EXPIRED: 'This transfer link has expired. Please request a new one.',
    COMPANY_ALREADY_CLAIMED: 'This company profile has already been claimed.',
    EMAIL_TRANSFER_MISMATCH: (email: string) => `Email does not match the transfer recipient (${email}).`,
    EMAIL_ALREADY_ASSOCIATED_WITH_CLAIMED_PROFILE: 'This email is already associated with a claimed CDMO profile. To claim this profile, you must first unclaim or transfer your existing profile',
    FAILED_LOAD_PROFILE: 'Failed to load profile data. Please try again.',
    FAILED_TO_TRIGGER_ADMIN_CLAIM_EMAIL: 'Failed to trigger admin claim email',
    LOGIN_REQUIRED_ACTION: (action: string) => `You must be logged in to ${action} your profile.`,
    GENERIC_SUBMISSION_ERROR: (action: string) => `There was an error ${action} your profile. Please try again.`,
    DOMAIN_MISMATCH_ERROR: 'Email domain mismatch',
    RECIPIENT_NAME_REQUIRED: 'Recipient name is required.',
    RECIPIENT_NAME_TOO_LONG: 'Recipient name cannot exceed 250 characters.',
    RECIPIENT_PHONE_REQUIRED: 'Recipient phone is required.',
    TRANSFER_PROFILE_ALREADY_CLAIMED: 'This CDMO profile has already been claimed by another user during the process. Ownership transfer is no longer available.',
    SPEED_TO_START_INVALID: 'Project kickoff timing must be one of the listed options.',
    FACILITY_LOCATION_TOO_LONG: (max: number) => `Facility location cannot exceed ${max} characters.`,
    FACILITY_EQUIPMENT_TOO_LONG: (max: number) => `Manufacturing equipment cannot exceed ${max} characters.`,
    FEI_NUMBER_INVALID: 'FEI number must be between 7 and 10 digits and contain only numbers.',
    FEI_NUMBER_ALREADY_REGISTERED: 'FEI number already exists',
    FEI_NUMBER_DUPLICATE_IN_FORM: 'This FEI number is already entered in this form',
    FEI_DELETE_FAILED: 'Could not delete FEI number from database.',
    VALIDATION_UNEXPECTED_ERROR: 'An unexpected error occurred during validation. Please try again.',

    // FEI number delete confirmation
    FEI_DELETE_CONFIRM_TITLE: 'Delete FEI number',
    FEI_DELETE_CONFIRM_MESSAGE: 'Are you sure you want to delete this FEI number? This cannot be undone.',
    FEI_DELETE_CONFIRM_BUTTON: 'Yes, Delete',

    /** Shown under a locked CDMO profile field for non-admin users */
    LOCKED_FIELD_DISCLAIMER: `This field is locked. Contact the admin ${SUPPORT_EMAIL} to unlock.`,

    CLAIM_LINK_COPIED: 'Permanent claim link copied to clipboard!',
    CLAIM_LINK_COPY_FAILED: 'Failed to copy link',
    NO_ADMIN_CLAIM_TOKEN: 'No admin claim token available',
    FIX_FOLLOWING_ERRORS: 'Please fix the following errors: ',
    // Must match the literal prefix thrown by dbService.createUserProfile (and other
    // dbService validate-then-throw methods) in src/lib/supabase.ts.
    VALIDATION_FAILED_PREFIX: 'Validation failed: ',
    EMAIL_ALREADY_REGISTERED_FOR_CLAIM: 'You already have an account with this email address. Please login to complete your claim or ownership transfer.',

    // Auth / signup errors
    INVALID_LOGIN_CREDENTIALS: 'Invalid email or password. Please check your credentials and try again.',
    PASSWORD_TOO_SHORT: 'Password must be at least 6 characters long.',
    INVALID_EMAIL_FORMAT: 'Please enter a valid email address.',
    USERNAME_ALREADY_TAKEN: 'This username is already taken. Please choose a different username.',
    USERNAME_ALREADY_TAKEN_INLINE: 'This username is already taken',
    EMAIL_ALREADY_REGISTERED: 'This email is already registered. Please use a different email.',
    EMAIL_ALREADY_REGISTERED_INLINE: 'This email is already registered',
    EMAIL_ALREADY_REGISTERED_INLINE_LOGIN: 'This email is already registered. Please login.',
    AUTH_ISSUE: 'There was an authentication issue. Please try again or contact support.',
    TOO_MANY_REQUESTS: 'Too many requests. Please wait a moment before trying again.',
    SIGN_IN_ERROR: 'There was an error signing in. Please try again.',
    CREATE_ACCOUNT_ERROR: 'There was an error creating your account. Please try again.',
    ADDITIONAL_EMAIL_DIFFERENT_FROM_PRIMARY: (index: number) => `Additional Email ${index + 2} must be different from the Primary Contact Email`,
    ADDITIONAL_EMAIL_DUPLICATE: (index: number) => `Additional Email ${index + 2} is a duplicate — each email must be unique`,
    ADDITIONAL_EMAIL_SAME_AS_PRIMARY: (index: number) => `Additional Email ${index + 2} is the same as the Primary Contact Email — each email must be unique`,
    ADDITIONAL_EMAIL_INVALID: (index: number, error: string) => `Additional Email ${index + 2}: ${error}`,
    WEBSITE_REQUIRED: 'Valid website is required',
    EMAIL_REQUIRED: 'Valid email is required',
    PHONE_REQUIRED: 'Valid phone number is required',
    PRIMARY_CONTACT_EMAIL_REQUIRED: 'Valid primary contact email is required',
    EMAIL_DOMAIN_MATCH_WEBSITE: 'Email domain must match website domain',
    PRIMARY_EMAIL_DOMAIN_MATCH_WEBSITE: 'Primary email domain must match website domain',
    EMAIL_DIFFERENT_FROM_PRIMARY: 'Email Address must be different from the Primary Contact Email',
    PRIMARY_DIFFERENT_FROM_EMAIL: 'Primary Contact Email must be different from the Email Address',
    ADDITIONAL_EMAIL_DIFFERENT_FROM_ACCOUNT: (index: number) => `Additional Email ${index + 2} must be different from the Email Address`,

    // Admin force transfer messages
    ADMIN_FORCE_TRANSFER_EMAIL_ALREADY_ASSOCIATED: (companyName: string) =>
        `Email already associated with "${companyName}"`,
    ADMIN_FORCE_TRANSFER_SUCCESS: 'Ownership has been transferred successfully. Notification emails have been sent.',
    ADMIN_FORCE_TRANSFER_FAILED: 'Failed to update CDMO profile ownership',
    ADMIN_FORCE_TRANSFER_CONFIRM_TITLE: 'Confirm Ownership Transfer',
    CDMO_PROFILE_NOT_FOUND: 'CDMO profile not found',

    // Admin force unclaim messages
    ADMIN_FORCE_UNCLAIM_CONFIRM_TITLE: 'Confirm Unclaim',
    ADMIN_FORCE_UNCLAIM_SUCCESS: 'Profile has been unclaimed. A permanent claim link has been generated and the admin has been notified.',
    ADMIN_FORCE_UNCLAIM_FAILED: 'Failed to unclaim CDMO profile. Please try again.',
    ADMIN_FORCE_UNCLAIM_NO_ID: 'No CDMO profile ID found. Please save the profile first.',

    // Admin-created claimed profile email invite
    ADMIN_CREATED_CLAIMED_NOTIFICATION_SENT: 'Profile created and a claim invitation email has been sent to the designated CDMO contact.',
    ADMIN_CREATED_CLAIMED_NOTIFICATION_FAILED: 'Profile created successfully, but failed to send the claim invitation email. Please resend manually.',
    CLAIM_RESTRICTED_TO_EMAIL: (email: string) => `This profile can only be claimed by ${email}. Please use that exact email address to complete the claim.`,

    // Common Modal Titles & Messages
    MODAL_TITLES: {
        LINK_PROBLEM: 'Link Problem',
        LINK_EXPIRED: 'Link Expired',
        ALREADY_CLAIMED: 'Account Already Claimed',
        TRANSFER_INITIATED: 'Transfer Initiated',
        SUCCESS: 'Success',
        ERROR: 'Error',
        UNCLAIM_FAILED: 'Unclaim Failed',
        TRANSFER_FAILED: 'Transfer Failed'
    },
    MODAL_MESSAGES: {
        UNCLAIM_SUCCESS_WITH_LOGIN: 'Account unclaimed successfully. Please log in again to access your new account.',
        UNCLAIM_FAILED_GENERIC: 'Failed to unclaim profile. Please try again.',
        TRANSFER_FAILED_GENERIC: 'Failed to initiate transfer. Please try again.'
    },

    // Unsubscribe page (messages only – used in setMessage)
    UNSUBSCRIBE_PAGE: {
        MISSING_LINK: 'Missing unsubscribe link. Please use the link from your email.',
        LINK_INVALID_OR_USED: 'This unsubscribe link is invalid or has already been used.',
        SOMETHING_WRONG: 'Something went wrong. Please try again or contact support.'
    }
} as const;

/** Copy for the admin CDMO bulk upload (CSV → unclaimed, admin-created profiles). */
export const CDMO_IMPORT_MESSAGES = {
    OPEN_BUTTON: 'Bulk upload CDMOs',
    PAGE_TITLE: 'Bulk upload CDMOs',
    BACK_TO_DASHBOARD: 'Back to CDMO Dashboard',
    NOT_AUTHORIZED:
        'Bulk uploading CDMO profiles is limited to administrators. Contact support if you believe you should have access.',
    STEP_SUBTITLES: {
        upload: 'Upload a CSV made with the AI-ready template. Nothing is saved until you confirm.',
        preview: 'Review every row before anything is created.',
        importing: 'Creating profiles. Keep this window open.',
        result: 'Import finished.',
    },
    DROP_TITLE: 'Drag and drop your CSV file here',
    DROP_HINT: 'CSV only · up to 500 CDMOs · 10 MB max',
    BROWSE: 'Browse files',
    READING_FILE: 'Reading and checking your file…',
    AI_TEMPLATE_LINK: 'Download AI-ready template',
    AI_TEMPLATE_TITLE: 'Using Claude or ChatGPT?',
    AI_TEMPLATE_HINT: 'Give it the AI-ready Excel template with your list of CDMOs, and it returns a CSV ready to upload here.',
    AI_TEMPLATE_PREPARING: 'Preparing template…',
    AI_TEMPLATE_FAILED: 'The AI-ready template could not be created. Please try again.',
    DUPLICATE_LOOKUP_FAILED: (reason: string) =>
        `Could not check for existing profiles (${reason}). Each row is still checked just before it is created.`,
    EXCLUDE_WARNINGS: (count: number) => `Leave out rows with warnings (${count})`,
    NOTHING_TO_IMPORT: 'No rows can be imported — fix the file and upload it again.',
    CREATE_BUTTON: (count: number) => `Create ${count} CDMO profile${count === 1 ? '' : 's'}`,
    CONFIRM_TITLE: (count: number) => `Create ${count} CDMO profile${count === 1 ? '' : 's'}?`,
    CONFIRM_POINTS: [
        'Each profile is created as Unclaimed and admin-created, with no owner.',
        'No emails are sent and nothing is enrolled in a campaign. Invite them later from Campaign.',
        'Every row, including skipped ones, is recorded in the import audit log.',
    ],
    CONFIRM_ACTION: (count: number) => `Create ${count} profile${count === 1 ? '' : 's'}`,
    IMPORTING_TITLE: 'Creating CDMO profiles…',
    IMPORTING_HINT: 'Keep this window open until the import finishes.',
    LEAVE_WARNING: 'CDMO profiles are still being created. Leaving now stops the import part-way.',
    ERROR_REPORT_BUTTON: 'Download error report',
    WARNINGS_REPORT_BUTTON: 'Download warnings report',
    UPLOAD_ANOTHER: 'Upload another file',
    DONE: 'Done',
    AUDIT_FAILED_TITLE: 'The profiles were created, but the import audit log could not be saved.',
    IMPORT_SUCCESS_TOAST: (count: number) => `${count} CDMO profile${count === 1 ? '' : 's'} created`,
    IMPORT_NONE_TOAST: 'No CDMO profiles were created',
    UNEXPECTED_IMPORT_ERROR: 'The import stopped unexpectedly. Check the CDMO list before trying again.',
} as const;
